import { Box, Flex, Icon, IconButton, Text } from '@chakra-ui/react';
import { AddShoppingCartOutlined, CheckCircle } from '@mui/icons-material';

import { type ProductPromoOffer } from '../promo-preview';

interface PromoSectionProps {
  offers: ProductPromoOffer[];
  effectiveMagnifiedQty: number;
  maxAllowedQuantity: number;
  onUseQuantity: (qty: number) => void;
}

function splitText(text: string): [string, string] {
  const parts = text.split(' ');
  if (parts[0]?.toLowerCase() === 'beli' && parts.length > 2) {
    return [parts.slice(0, 2).join(' '), parts.slice(2).join(' ')];
  }
  return [text, ''];
}

export function PromoSection({
  offers,
  effectiveMagnifiedQty,
  maxAllowedQuantity,
  onUseQuantity,
}: PromoSectionProps) {
  if (offers.length === 0) return null;

  return (
    <Box mb={4}>
      <Text fontSize="xs" fontWeight="semibold" color="#00003D" mb={2}>
        Solusi Hemat!
      </Text>
      <Flex direction="column" gap={2}>
        {offers.map((offer, index) => {
          const isActive =
            offer.type !== 'bundle' &&
            !!offer.targetQuantity &&
            offer.targetQuantity <= effectiveMagnifiedQty;
          const canUse =
            offer.type !== 'bundle' &&
            !!offer.targetQuantity &&
            offer.targetQuantity <= maxAllowedQuantity * 1000 &&
            !isActive;
          const isFreeIcon =
            offer.type === 'free-item' ||
            (offer.type === 'bundle' &&
              offer.text.startsWith('Beli') &&
              offer.text.includes('Gratis'));
          const [prefix, highlight] = splitText(offer.text);
          const targetQty = offer.targetQuantity;
          const handleRowClick =
            canUse && targetQty ? () => onUseQuantity(targetQty) : undefined;

          return (
            <Flex
              key={`${offer.type}-${index}`}
              align="center"
              gap={2}
              bgColor={isActive ? '#F1FFF1' : '#F1F8FF'}
              rounded="md"
              px={3}
              py={2}
              cursor={handleRowClick ? 'pointer' : 'default'}
              onClick={handleRowClick}
              transition="background-color 0.15s ease"
              _hover={
                handleRowClick
                  ? { bgColor: '#E4EEFF' }
                  : isActive
                  ? { bgColor: '#E3F8E3' }
                  : undefined
              }
            >
              <Box flexShrink={0}>
                <img
                  src={
                    isFreeIcon
                      ? '/assets/icons/free.svg'
                      : '/assets/icons/discount.svg'
                  }
                  alt={isFreeIcon ? 'Free' : 'Diskon'}
                  width={isFreeIcon ? 38 : 24}
                  height={isFreeIcon ? 20 : 24}
                />
              </Box>
              <Text flex="1" minW={0} fontSize="sm" noOfLines={2}>
                <Text as="span" color="#00003D" fontWeight="medium">
                  {prefix}
                </Text>
                {highlight && (
                  <Text as="span" color="#FF0000" fontWeight="semibold">
                    {' '}
                    {highlight}
                  </Text>
                )}
              </Text>
              {isActive ? (
                <Icon
                  as={CheckCircle}
                  boxSize={5}
                  color="#34C759"
                  flexShrink={0}
                />
              ) : canUse ? (
                <IconButton
                  aria-label={`pakai promo ${offer.text}`}
                  icon={<Icon as={AddShoppingCartOutlined} boxSize={5} />}
                  variant="ghost"
                  color="#0000A3"
                  size="sm"
                  // Row already owns the click; button stays for visual affordance.
                  // Stop propagation so onUseQuantity does not fire twice.
                  onClick={(e) => {
                    e.stopPropagation();
                    if (offer.targetQuantity) onUseQuantity(offer.targetQuantity);
                  }}
                />
              ) : null}
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
}
